import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import * as api from "./api";
import type {
  RouteImportPayload,
  TechCalcDrawing,
  TechCalcVariant,
  VariantResults,
} from "./types";

const qk = {
  projects: ["tc", "projects"] as const,
  project: (id: string) => ["tc", "project", id] as const,
  drawing: (v: string) => ["tc", "drawing", v] as const,
  results: (v: string) => ["tc", "results", v] as const,
  dictionaries: ["tc", "dictionaries"] as const,
  routeSources: ["tc", "route-sources"] as const,
  norms: (kind: api.NormKind) => ["tc", "norms", kind] as const,
  normChanges: ["tc", "norm-changes"] as const,
};

// ── projects ─────────────────────────────────────────────────────────────────
export const useTechCalcProjects = () =>
  useQuery({ queryKey: qk.projects, queryFn: api.getProjects });

export const useTechCalcProject = (id: string | undefined) =>
  useQuery({
    queryKey: qk.project(id ?? ""),
    queryFn: () => api.getProject(id!),
    enabled: !!id,
  });

export const useCreateTechCalcProject = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: { name: string; description: string }) => api.createProject(payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.projects }),
  });
};

export const usePatchTechCalcProject = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: Partial<{ name: string; description: string }> }) =>
      api.patchProject(id, payload),
    onSuccess: (_, { id }) => {
      qc.invalidateQueries({ queryKey: qk.projects });
      qc.invalidateQueries({ queryKey: qk.project(id) });
    },
  });
};

export const useDeleteTechCalcProject = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.deleteProject(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.projects }),
  });
};

// ── variants ─────────────────────────────────────────────────────────────────
export const useCreateVariant = (projectId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (name: string) => api.createVariant(projectId, name),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.project(projectId) }),
  });
};

export const useDuplicateVariant = (projectId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, name }: { id: string; name?: string }) => api.duplicateVariant(id, name),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.project(projectId) }),
  });
};

export const usePatchVariant = (projectId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: Partial<Pick<TechCalcVariant, "name" | "access_lines_coeff">> }) =>
      api.patchVariant(id, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.project(projectId) }),
  });
};

// ── import trasy z projektu ZE ───────────────────────────────────────────────
/** Projekty ZE z wariantami tras. Pobierane dopiero po otwarciu formularza
 *  (`enabled`) — poza nim nikt tej listy nie potrzebuje, a bywa długa. */
export const useRouteSources = (enabled: boolean) =>
  useQuery({
    queryKey: qk.routeSources,
    queryFn: api.getRouteSources,
    enabled,
    staleTime: 5 * 60_000,
  });

export const useImportRouteVariant = (projectId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: RouteImportPayload) => api.importRouteVariant(projectId, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.project(projectId) }),
  });
};

// ── drawing ──────────────────────────────────────────────────────────────────
export const useDrawing = (variantId: string | undefined) =>
  useQuery({
    queryKey: qk.drawing(variantId ?? ""),
    queryFn: () => api.getDrawing(variantId!),
    enabled: !!variantId,
  });

export const useSaveDrawing = (variantId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (fc: TechCalcDrawing) => api.saveDrawing(variantId, fc),
    onSuccess: () => {
      // Saving replaces the drawing AND clears/staleness-flags the results.
      qc.invalidateQueries({ queryKey: qk.drawing(variantId) });
      qc.invalidateQueries({ queryKey: qk.results(variantId) });
    },
  });
};

export const useFetchAddresses = (variantId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (force?: boolean) => api.fetchAddresses(variantId, force ?? false),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.drawing(variantId) }),
  });
};

// ── computation ──────────────────────────────────────────────────────────────
export const useResults = (variantId: string | undefined) =>
  useQuery({
    queryKey: qk.results(variantId ?? ""),
    queryFn: () => api.getResults(variantId!),
    enabled: !!variantId,
    // A never-computed variant may 404 here — no point retrying.
    retry: false,
  });

export const useRecompute = (variantId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.recompute(variantId),
    onSuccess: (data) => {
      // The contract returns the fresh results directly — seed the cache with them,
      // falling back to an invalidate if the shape is unexpected.
      if (data && typeof data === "object" && "results" in data) {
        qc.setQueryData<VariantResults>(qk.results(variantId), data);
      }
      qc.invalidateQueries({ queryKey: qk.results(variantId) });
    },
  });
};

// ── dictionaries ─────────────────────────────────────────────────────────────
export const useDictionaries = () =>
  useQuery({ queryKey: qk.dictionaries, queryFn: api.getDictionaries, staleTime: 5 * 60_000 });

// ── norm catalog ─────────────────────────────────────────────────────────────
export const useNorms = (kind: api.NormKind) =>
  useQuery({ queryKey: qk.norms(kind), queryFn: () => api.getNorms(kind) });

export const useNormChanges = (enabled = true) =>
  useQuery({ queryKey: qk.normChanges, queryFn: api.getNormChanges, enabled });

const useInvalidateNorms = () => {
  const qc = useQueryClient();
  return (kind: api.NormKind) => {
    qc.invalidateQueries({ queryKey: qk.norms(kind) });
    qc.invalidateQueries({ queryKey: qk.normChanges });
  };
};

export const useCreateNorm = (kind: api.NormKind) => {
  const invalidate = useInvalidateNorms();
  return useMutation({
    mutationFn: (payload: Record<string, unknown>) => api.createNorm(kind, payload),
    onSuccess: () => invalidate(kind),
  });
};

export const usePatchNorm = (kind: api.NormKind) => {
  const invalidate = useInvalidateNorms();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: Record<string, unknown> }) =>
      api.patchNorm(kind, id, payload),
    onSuccess: () => invalidate(kind),
  });
};

export const useDeleteNorm = (kind: api.NormKind) => {
  const invalidate = useInvalidateNorms();
  return useMutation({
    mutationFn: (id: string) => api.deleteNorm(kind, id),
    onSuccess: () => invalidate(kind),
  });
};
